import axios from 'axios';
import { STEPS_SOLE_PROPRIETOR } from '../config';
import { REGISTRATION_STATES } from '../../Text/config/registrationStatus';

const initialState = {
  registerForm: null,
  campaignForm: {
    industry: 'REAL_ESTATE',
    useCase:
      'Marketing messaging and communications between real estate agents and their contacts database.',
    sample1:
      'Hello {recipient name} this is {agent name}. A new property has hit the market in your area, get more information by responding here. Reply STOP to OPT Out.',
    sample2:
      "Hi {recipient name} this is {agent name}. If you're thinking of buying or selling a property, we're here to guide you every step of the way. Let's schedule a consultation to discuss your goals and explore the current market trends.  Reply STOP to OPT Out",
  },
  preloadedCampaignForm: {
    industry: null,
    useCase: null,
    sample1: null,
    sample2: null,
  },
  step: STEPS_SOLE_PROPRIETOR.SOLE_PROPRIETOR,
  currentState: null,
  currentSubstate: null,
  isLoading: false,
  successRegistration: false,
  failedRegistration: false,
  showRegistrationFlowModal: false,
  registrationModalClose: '',
};

const normalizePhoneNumber = (phone) => {
  var digits = phone.match(/\d/g);
  if (digits && digits.length > 10) {
    return digits.slice(-10).join("");
  }

  if (digits) {
    return digits.join("");
  }

  return "";
}

const getRegistrationPayload = (registerForm, campaignForm) => {
  const countryCode = '+1';

  const payload = {
    business_information: {
      team_size: '1-10',
      vertical: campaignForm.industry,
    },
    address: {
      street: registerForm.street,
      city: registerForm.city,
      region: registerForm.state,
      postal_code: registerForm.zipCode,
      iso_country: registerForm.country,
    },
    campaign: {
      campaign_description: campaignForm.useCase,
      message_sample_1: campaignForm.sample1,
      message_sample_2: campaignForm.sample2,
      us_app_to_person_use_case: 'SOLE_PROPRIETOR',
      has_embedded_links: true,
      has_embedded_phone: true,
    },
    customer_information: {
      last_name: registerForm.lastName,
      first_name: registerForm.firstName,
      phone_number: `${countryCode}${registerForm.phone}`,
      email: registerForm.email,
      in_real_estate: !!registerForm.isBroker,
    },
  };

  return payload;
};

const parseRegistration = ({ registration_data }) => {
  if (!registration_data || !registration_data['Valid']) return null;

  const {
    business_information,
    address,
    customer_information,
    campaign,
  } = JSON.parse(registration_data['String']);

  let isBroker = customer_information.in_real_estate === undefined ? true : customer_information.in_real_estate;

  return {
    customer: {
      isBroker: isBroker ? 1 : 0,
      street: address.street,
      city: address.city,
      state: address.region,
      zipCode: address.postal_code,
      country: address.iso_country,
      firstName: customer_information.first_name,
      lastName: customer_information.last_name,
      phone: normalizePhoneNumber(customer_information.phone_number),
      email: customer_information.email,
    },
    campaign: {
      industry: business_information.vertical || '',
      useCase: campaign.campaign_description,
      sample1: campaign.message_sample_1,
      sample2: campaign.message_sample_2,
    },
  };
};

export default {
  namespaced: true,
  state: { ...initialState },
  mutations: {
    setForm(store, { form }) {
      store.registerForm = form;
    },
    setStep(store, step) {
      store.step = step;
    },
    setCampaignForm(store, { form }) {
      store.campaignForm = form;
    },
    setState(store, { state, subState }) {
      store.currentState = state;
      store.currentSubstate = subState;
    },
    setIsLoading(store, isLoading) {
      store.isLoading = isLoading;
    },
    setSuccessRegistration(store, value) {
      store.successRegistration = value;
    },
    setFailedRegistration(store, message) {
      store.failedRegistration = message;
    },
    setShowRegistrationFlowModal(store, boolean) {
      store.showRegistrationFlowModal = boolean;
    },
    setregistrationModalClose(store, action) {
      store.registrationModalClose = action;
    },
    setPreloadedCampaignForm(store, { form }) {
      store.preloadedCampaignForm = form;
    },
  },
  actions: {
    setStep({ commit }, step) {
      commit('setStep', step);
    },
    setForm({ commit }, form) {
      commit('setForm', form);
    },
    setCampaignForm({ commit }, form) {
      commit('setCampaignForm', form);
    },
    setState({ commit }, state, subState) {
      commit('setState', state, subState);
    },
    setShowRegistrationFlowModal({ commit }, { bool, action }) {
      commit('setShowRegistrationFlowModal', bool);
      commit('setregistrationModalClose', action || '');
    },
    resetCampaignForm({commit}) {
      commit('setCampaignForm', { form: initialState.campaignForm });
    },
    setPreloadedCampaignForm({ commit }, form) {
      commit('setPreloadedCampaignForm', form);
    },
    async sendSoleProprieterRegistration({ rootState, state, commit }) {
      commit('setIsLoading', true);
      try {
        const payload = getRegistrationPayload(state.registerForm, state.campaignForm);
        if (
          [REGISTRATION_STATES.CAMPAIGN_TCR_FAILED,
          REGISTRATION_STATES.CAMPAIGN_TWILIO_FAILED,
          REGISTRATION_STATES.BRAND_FAILED,
          REGISTRATION_STATES.CUSTOMER_PROFILE_FAILED,
          REGISTRATION_STATES.BRAND_OTP_FAILED,
          REGISTRATION_STATES.A2P_BUNDLE_FAILED,
          REGISTRATION_STATES.BRAND_TCR_FAILED,
          ].includes(
            rootState.conversations.registrationStatus10dlc,
          )
        ) {
          await axios.put(
            `${process.env.VUE_APP_COMMUNICATIONS_API_BASE_URL}/register-10dlc/sole_proprietor`,
            payload,
          );
        } else {
          await axios.post(
            `${process.env.VUE_APP_COMMUNICATIONS_API_BASE_URL}/register-10dlc/sole_proprietor`,
            payload,
          );
        }
        commit('setSuccessRegistration', true);
      } catch (error) {
        commit('setFailedRegistration', true);
      } finally {
        commit('setIsLoading', false);
      }
    },
    async getRegistrationData({ commit }, registrationType) {
      commit('setIsLoading', true);
      try {
        const res = await axios.get(
          `${process.env.VUE_APP_COMMUNICATIONS_API_BASE_URL}/register-10dlc/info/${registrationType}`,
        );

        const formData = parseRegistration(res.data);
        if (formData) {
          commit('setForm', { form: formData.customer });
          commit('setPreloadedCampaignForm', { form: formData.campaign });
        }
      } catch (error) {
        console.error(error);
      } finally {
        commit('setIsLoading', false);
      }
    },
  },
};
